
import { useMemo, useRef } from 'react';
import { useMultiWealthica2Store } from '@/stores/multiWealthica2Store';
import { logger } from '@/services/LoggingService';
import { supabase } from '@/integrations/supabase/client';
import { PerformanceMonitor } from '@/utils/performance-monitor';
import type { WealthicaAccount } from '../types';
import { isDemoProvider } from './identifiers';
import { isDebugEnabled } from '@/utils/debug';

/**
 * Helper function to detect demo provider by characteristics
 */
export function isDemo(institutionId: string, providerName?: string): boolean {
  return isDemoProvider(providerName, undefined, institutionId);
}

/**
 * Enriches account data with institution metadata from the database
 */
export async function enrichAccountsWithInstitutionData(accounts: WealthicaAccount[], institutionId: string): Promise<WealthicaAccount[]> {
  try {
    logger.info('enrichAccountsWithInstitutionData', 'Starting institution metadata enrichment', {
      accountCount: accounts?.length || 0,
      institutionId
    });

    // Handle demo provider with characteristic-based detection
    if (isDemo(institutionId)) {
      logger.info('enrichAccountsWithInstitutionData', 'Demo provider detected, using demo metadata', {
        institutionId
      });
      const institutionName = 'Wealthica Demo';
      const institutionDisplayName = `${institutionName} (${accounts?.length || 0} accounts)`;
      
      return accounts.map(account => ({
        ...account,
        institutionName,
        institutionDisplayName,
        institutionLogoUrl: null
      }));
    }


    // Only check mappings for potential demo institutions
    // Production institutions use their institution_id directly as stable identifier
    let stableIdentifier = institutionId;
    
    // Demo detection logic - only query mappings if this might be a demo institution
    const isPotentiallyDemo = institutionId === 'demo' || 
                             institutionId.includes('demo') ||
                             institutionId.length < 20; // Demo IDs tend to be shorter
    
    if (isPotentiallyDemo) {
      const mappingsQuery = supabase
        .from('institution_id_mappings')
        .select('stable_identifier')
        .eq('wealthica_institution_id', institutionId)
        .eq('provider_name', 'wealthica') as any;
      const { data: mappingData, error: mappingError } = await mappingsQuery.maybeSingle();

      if (mappingError) {
        logger.warn('enrichAccountsWithInstitutionData', 'Error fetching demo institution mapping', { 
          error: mappingError,
          institutionId 
        });
      }

      // Use mapping if found, otherwise fall back to institution_id
      stableIdentifier = mappingData?.stable_identifier || institutionId;
    } else {
      logger.debug('enrichAccountsWithInstitutionData', 'Using production institution ID directly as stable identifier', {
        institutionId
      });
    }
    
    // Then get the institution metadata (use maybeSingle to avoid errors)
    const { data: institutionData, error: institutionError } = await supabase
      .from('investment_institutions')
      .select('institution_name, logo_url, logo_proxied_url')
      .eq('institution_identifier', stableIdentifier)
      .eq('provider_type', 'wealthica')
      .maybeSingle();

    if (institutionError) {
      logger.warn('enrichAccountsWithInstitutionData', 'Error fetching institution data', { 
        error: institutionError,
        stableIdentifier 
      });
    }

    // Determine the display name and logo
    let institutionName = 'Unknown Institution';
    let logoUrl: string | null = null;

    if (institutionData) {
      institutionName = institutionData.institution_name;
      logoUrl = institutionData.logo_proxied_url || institutionData.logo_url;
    } else {
      institutionName = 'Financial Institution';
    }

    // Create display name with account count
    const institutionDisplayName = `${institutionName} (${accounts?.length || 0} accounts)`;

    logger.info('enrichAccountsWithInstitutionData', 'Institution metadata resolved', {
      institutionName,
      hasLogo: !!logoUrl,
      stableIdentifier
    });

    // Enrich all accounts with the institution metadata
    return accounts.map(account => ({
      ...account,
      institutionName,
      institutionDisplayName,
      institutionLogoUrl: logoUrl
    }));

  } catch (error) {
    logger.error('enrichAccountsWithInstitutionData', 'Failed to enrich accounts with institution data', { error });
    // Return accounts with fallback institution data
    const fallbackName = isDemo(institutionId) ? 'Wealthica Demo' : 'Financial Institution';
    return accounts.map(account => ({
      ...account,
      institutionName: fallbackName,
      institutionDisplayName: `${fallbackName} (${accounts?.length || 0} accounts)`,
      institutionLogoUrl: null
    }));
  }
}

/**
 * Store-based hook for read-only account data access
 * Used when persist=false and no custom parameters
 * Supports both singleton and multi-institution stores via feature flags
 */
export function useAccountsFromStore() {
  // Cutover: enforce multi-store only (flags removed)
  // Multi-store: subscribe only to primitives
  const connectionSize = useMultiWealthica2Store((s) => s.connections.size);
  const activeConnectionId = useMultiWealthica2Store((s) => s.activeConnectionId);
  // Sum of accounts across all connections (primitive) to detect arrival of accounts
  const accountsCountPrimitive = useMultiWealthica2Store((s) => {
    let count = 0;
    s.connections.forEach((c) => { count += (c.accounts?.length || 0); });
    return count;
  });
  // Lightweight checksum of balances (primitive) to detect value changes without arrays
  const balancesChecksumPrimitive = useMultiWealthica2Store((s) => {
    let sum = 0;
    s.connections.forEach((c) => {
      (c.accounts || []).forEach((a: Partial<WealthicaAccount> & { balance?: number | string }) => {
        const raw = a.balance;
        const v = typeof raw === 'number' ? raw : typeof raw === 'string' ? parseFloat(raw || '0') : 0;
        if (!Number.isNaN(v)) sum += v;
      });
    });
    return Math.round(sum * 100); // minor stability against float jitter
  });
  // Any loading flag (primitive) for UI loading state
  const anyLoadingPrimitive = useMultiWealthica2Store((s) => {
    for (const c of s.connections.values()) if (c.isLoadingAccounts) return true;
    return false;
  });

  // Derive values with getState() inside memo to avoid unstable snapshots
  const { accounts, isLoadingAccounts, accountsError, connectionData, isConnected } = useMemo(() => {
    const state = useMultiWealthica2Store.getState();
    const connections = Array.from(state.connections.values());
    const allAccounts = connections.flatMap((c) => c.accounts || []);
    const anyLoading = connections.some((c) => !!c.isLoadingAccounts);
    const aggregatedError = connections.find((c) => !!c.accountsError)?.accountsError || null;
    const connData = activeConnectionId ? state.getConnection(activeConnectionId)?.connectionData : (state.getAllConnections()[0]?.connectionData || null);
    const anyConnected = connections.some((c) => c.isConnected);
    return {
      accounts: allAccounts,
      isLoadingAccounts: anyLoading,
      accountsError: aggregatedError,
      connectionData: connData,
      isConnected: anyConnected
    };
  // Recompute when the number of connections changes or active id changes
  }, [connectionSize, activeConnectionId, accountsCountPrimitive, balancesChecksumPrimitive, anyLoadingPrimitive]);
  
  // 🔇 Log throttling: Only log once per hook instance to prevent spam
  const hasLoggedStore = useRef(false);
  if (!hasLoggedStore.current && isDebugEnabled()) {
    console.log('📊 ACCOUNTS HOOK - Using store path (read-only consumer)', {
      storeMode: 'multi-store',
      accountCount: accounts?.length || 0,
      isLoading: isLoadingAccounts,
      hasError: !!accountsError,
      isConnected,
      source: 'MULTI_ZUSTAND_STORE'
    });
    hasLoggedStore.current = true;
  }

  // === PHASE 3: PERFORMANCE VERIFICATION ===
  // Log that this call was routed to store (not API)
  PerformanceMonitor.logStoreCall({
    accountCount: accounts?.length || 0,
    isLoading: isLoadingAccounts,
    hasError: !!accountsError,
    isConnected
  });

  // Return interface compatible with full hook
  return {
    wealthicaAccounts: accounts || [],
    isWealthicaConnected: isConnected,
    isWealthicaLoading: isLoadingAccounts,
    wealthicaError: accountsError ? new Error(accountsError) : null,
    isWealthicaRefetching: false,
    institutionId: connectionData?.institutionId,
    providerName: connectionData?.providerName,
    connectionId: connectionData?.connectionId,
    lastSyncAt: connectionData?.lastSyncAt,
    hasTokenExpired: false,
    hasPersistedData: false
  };
}
